/* eslint-disable react/prop-types */
import FormControl from '@material-ui/core/FormControl'
import FormHelperText from '@material-ui/core/FormHelperText'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import dayjs from 'dayjs'
import { useFormikContext } from 'formik'
import { isEmpty } from 'lodash'
import React, { useState } from 'react'

import {
  fetchInteractionConnectedStatusOptions,
  fetchInteractionModeOptions,
  fetchAllInteractionTopicOptions,
  fetchInteractionTopicOptions,
  fetchSubject
} from 'api'
import FollowUpWidget from 'app/components/FollowUpWidget'
import { Select } from 'app/components/generic/FormElements'
import { DatePicker } from 'app/components/generic/FormFields'
import NestedFieldWidget from 'app/components/NestedFieldWIdget'
import { INTERACTION_TEMPLATES } from 'app/pages/Communication/InteractionHistory/templates'
import { validateNotEmpty } from 'app/validators'

// THE interaction field set — one definition, rendered by every surface that
// logs or edits a CRM interaction:
//
//   * the parent dashboard's edit-interaction form  (forms/EditInteractionForm)
//   * the Grow & Retain drawer                      (RevenueGoals/InteractionDrawer)
//
// It used to exist only on the parent dashboard, and the Revenue Goals drawer
// carried a hand-rolled lighter copy: one topic instead of many, plain text
// instead of the WYSIWYG, no voice capture, and — the one that actually lost
// data — no follow-up question at all, so a callback promised during a Grow &
// Retain call was never recorded and never reached the Follow-ups tab or the
// "Clear callbacks due" target (user 2026-08-21). Sharing the component is what
// stops the two drifting apart again.
//
// Order + visibility (user 2026-09-24): the TOPIC is picked first, before the
// Connected Status, and on every outcome — a DNP still has a reason it was
// dialled, and chase.py derives a parent's contactability ('dnp', then
// 'unreachable' after three) from the latest interaction carrying the
// 'Recharge' topic. The summary / per-topic template and the channel stay
// Connected-only; the follow-up question shows for Connected and DNP. The
// Interaction History page applies the same rules. Mandatory-ness is enforced
// by the caller's save hook — see `isInteractionFollowUpAnswered` and
// `requiredFollowUpDateProblem` below, which every caller uses.
//
// The summary sits in the same NestedFieldWidget row as its topic, so it
// renders ABOVE the Connected Status rather than after the channel: splitting
// the rows into two widgets would lose the topic heading on each summary and
// needs changes to NestedFieldWidget itself.
//
// REQUIRES a Formik context: NestedFieldWidget and the FormElements are all
// Formik `<Field>`s.

// The follow-up value encodes its own answered-ness (see FollowUpWidget):
//   '' / undefined -> unanswered   | 'yes' -> yes, date pending  => INVALID
//   null           -> no follow-up | 'YYYY-MM-DD' -> yes + date  => valid
export const isInteractionFollowUpAnswered = (value) =>
  value === null || /^\d{4}-\d{2}-\d{2}$/.test(value || '')

export const INTERACTION_FOLLOW_UP_STATUSES = ['Connected', 'DNP']

// Recharge "Will recharge" = Yes / Undecided makes the follow-up date
// compulsory (user 2026-09-24). Defined next to the templates it reads.
export {
  followUpRequiredByTopics,
  requiredFollowUpDateProblem
} from 'app/pages/Communication/InteractionHistory/templates'

// The SUMMARY stays Connected-only while the topic shows on every outcome; for
// a non-Connected save the caller substitutes the topic for the summary
// (CrmInteractionSummary.summary is NOT NULL), which is what NestedFieldWidget
// already does when a template renders nothing.
const InteractionFields = ({ defaults }) => {
  const { errors, values, setFieldValue } = useFormikContext()

  const [connectedStatusOptions, setConnectedStatusOptions] = useState([])
  const [modeOptions, setModeOptions] = useState([])
  const [topicOptions, setTopicOptions] = useState({})
  // The CURRENT topic list (user 2026-09-24). topicOptions maps every topic
  // ever stored, so an old interaction's retired topic still displays, but
  // only these are offered (NestedFieldWidget `activeValues`).
  const [activeTopics, setActiveTopics] = useState(null)
  const [subjectOptions, setSubjectOptions] = useState([])

  // The three option lists are config-app keys, same as the create form reads.
  React.useEffect(() => {
    const loadOptions = async () => {
      try {
        const [statuses, modes, topics, allTopics] = await Promise.all([
          fetchInteractionConnectedStatusOptions(),
          fetchInteractionModeOptions(),
          fetchInteractionTopicOptions(),
          fetchAllInteractionTopicOptions()
        ])
        setConnectedStatusOptions(statuses?.results?.[0]?.value || [])
        setModeOptions(modes?.results?.[0]?.value || [])
        const active = topics?.results?.[0]?.value || []
        setActiveTopics(active)
        setTopicOptions(
          [...active, ...(allTopics?.results?.[0]?.value || [])].reduce((acc, option) => {
            acc[option] = option
            return acc
          }, {})
        )
      } catch (error) {
        console.error('Error fetching interaction options:', error)
      }
    }
    loadOptions()
  }, [])

  // Subject names for the "Program(s) interested in" multi-select inside the
  // New Program Enquiry template.
  React.useEffect(() => {
    const loadSubjects = async () => {
      try {
        const res = await fetchSubject({ queries: { limit: 600, offset: 0 } })
        const names = (res?.results || []).map((s) => s?.name).filter(Boolean)
        setSubjectOptions(Array.from(new Set(names)).sort())
      } catch (error) {
        console.error('Error fetching subjects:', error)
      }
    }
    loadSubjects()
  }, [])

  const isConnected = values?.connected_status === 'Connected'
  const showFollowUp = INTERACTION_FOLLOW_UP_STATUSES.includes(
    values?.connected_status
  )

  const onDateChangeHandler = (e) => {
    setFieldValue('interaction_date', e.target.value)
  }

  return (
    <>
      <FormControl
        variant="outlined"
        {...(!isEmpty(errors.interaction_date) ? { error: true } : null)}
        required
      >
        <DatePicker
          // An interaction can only have happened in the past.
          inputProps={{ max: dayjs().format('YYYY-MM-DD') }}
          name={'interaction_date'}
          label={'Interaction Date'}
          value={values?.interaction_date || ''}
          onChange={onDateChangeHandler}
          variant="outlined"
          required
          validate={validateNotEmpty({ isRequired: true })}
        />
        {!isEmpty(errors.interaction_date) && (
          <FormHelperText>{errors.interaction_date}</FormHelperText>
        )}
      </FormControl>

      {/* Topic first, on every outcome (user 2026-09-24) — see the header. */}
      <NestedFieldWidget
        fieldName="interaction_topics"
        label={isConnected ? 'interaction summary' : 'topic'}
        setFieldValue={setFieldValue}
        formikFieldData={values?.interaction_topics}
        config={{
          ctaLabel: '+ another topic',
          widgetTitle: isConnected ? 'interaction summary' : 'topic',
          templates: INTERACTION_TEMPLATES,
          subjectOptions,
          fields: [
            {
              name: 'topic',
              label: 'topic',
              type: 'select',
              uniqueOption: true,
              validate: validateNotEmpty,
              valueLabelMappings: topicOptions,
              activeValues: activeTopics
            },
            // Nothing was said on a call that was not answered, so the
            // summary editor is Connected-only even though the topic is not.
            ...(isConnected
              ? [
                  {
                    name: 'summary',
                    label: 'summary',
                    type: 'wysiwyg',
                    validate: validateNotEmpty
                  }
                ]
              : [])
          ]
        }}
      />

      <FormControl variant="outlined" required>
        <InputLabel>{'Connected Status'}</InputLabel>
        <Select
          name={'connected_status'}
          defaultValue={defaults?.connected_status || ''}
          label={'Connected Status'}
          validate={validateNotEmpty({ isRequired: true })}
        >
          {connectedStatusOptions?.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
        {!isEmpty(errors.connected_status) && (
          <FormHelperText>{errors.connected_status}</FormHelperText>
        )}
      </FormControl>

      {isConnected ? (
        <FormControl variant="outlined" required>
          <InputLabel>{'Interaction Channel'}</InputLabel>
          <Select
            name={'interaction_mode'}
            defaultValue={defaults?.interaction_mode || ''}
            label={'Interaction Channel'}
            validate={validateNotEmpty({ isRequired: true })}
          >
            {modeOptions?.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
          {!isEmpty(errors.interaction_mode) && (
            <FormHelperText>{errors.interaction_mode}</FormHelperText>
          )}
        </FormControl>
      ) : null}

      {showFollowUp ? (
        <FollowUpWidget
          fieldName="follow_up_date"
          label="Follow-up required?"
          setFieldValue={setFieldValue}
          formikFieldData={values?.follow_up_date}
        />
      ) : null}
    </>
  )
}

export default InteractionFields
